import Hero from '../components/Hero';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import { useState, useEffect } from 'react';
import { getServices, getTestimonials } from '../services/api';
import {
  company,
  clientTestimonials,
  futureGoals,
  leadership,
  partnerNetwork,
  profileServices,
  whyChooseUs,
} from '../content/companyContent';
import './Home.css';

function PartnerSlider({ title, partners, reverse = false }) {
  const sliderItems = [...partners, ...partners];

  return (
    <div className="partner-slider-block">
      <h3>{title}</h3>
      <div className="partner-slider" aria-label={title}>
        <div className={`partner-slider-track ${reverse ? 'reverse' : ''}`}>
          {sliderItems.map((partner, index) => (
            <article className="partner-card" key={`${partner.name}-${index}`}>
              <img src={partner.logo} alt={`${partner.name} logo`} />
              <span>{partner.name}</span>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function Home() {
  const [services, setServices] = useState(profileServices);
  const [testimonials, setTestimonials] = useState(clientTestimonials);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, testimonialsRes] = await Promise.all([
        getServices(),
        getTestimonials(),
      ]);
      const serviceData = servicesRes.data.results || servicesRes.data;
      const testimonialData = testimonialsRes.data.results || testimonialsRes.data;
      setServices(serviceData.length ? serviceData : profileServices);
      setTestimonials(testimonialData.length ? testimonialData : clientTestimonials);
    } catch (error) {
      console.error('Error fetching data:', error);
      setServices(profileServices);
      setTestimonials(clientTestimonials);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home">
      <Hero />

      <section className="intro-section">
        <div className="container intro-grid">
          <div>
            <p className="eyebrow">Integrated healthcare since {company.founded}</p>
            <h2>{company.motto}</h2>
            <p>{company.about}</p>
          </div>
          <div className="intro-facts" aria-label="Company highlights">
            <div>
              <strong>{company.founded}</strong>
              <span>Trusted legacy</span>
            </div>
            <div>
              <strong>{company.rebranded}</strong>
              <span>Expanded vision</span>
            </div>
            <div>
              <strong>24/7</strong>
              <span>Emergency support</span>
            </div>
          </div>
        </div>
      </section>

      <section className="services-section">
        <div className="container">
          <p className="eyebrow">Core services</p>
          <h2>Healthcare, logistics, and technology under one roof</h2>
          <p className="section-subtitle">Built for patients, hospitals, corporates, and healthcare partners.</p>
          
          {loading ? (
            <p className="loading">Loading services...</p>
          ) : (
            <div className="services-grid">
              {services.map(service => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="trust-section">
        <div className="container trust-grid">
          <div>
            <p className="eyebrow">Why choose us</p>
            <h2>Transparent, trained, and people-centric service delivery</h2>
            <ul className="check-list">
              {whyChooseUs.map(item => <li key={item}>{item}</li>)}
            </ul>
          </div>
          <div>
            <p className="eyebrow">Our mission</p>
            <h2>Professional care strengthened by technology</h2>
            <ul className="check-list">
              {company.mission.map(item => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="network-section">
        <div className="container">
          <p className="eyebrow">Partner network</p>
          <h2>Connected with trusted hospital and corporate partners</h2>
          <div className="partner-slider-stack">
            <PartnerSlider title="Hospital partners" partners={partnerNetwork.hospitals} />
            <PartnerSlider title="Corporate partners" partners={partnerNetwork.corporate} reverse />
          </div>
        </div>
      </section>

      <section className="testimonials-section">
        <div className="container">
          <p className="eyebrow">Feedback</p>
          <h2>What our clients say</h2>
          <p className="section-subtitle">Real experiences from partners and patients.</p>
          
          <div className="testimonials-grid">
            {testimonials.slice(0, 3).map(testimonial => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="container about-grid">
          
          <div>
            <p className="eyebrow">Future goals</p>
            <h2>Expanding access through diagnostics, ambulance networks, and AI tools</h2>
            <ul className="plain-list">
              {futureGoals.map(goal => <li key={goal}>{goal}</li>)}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
